"use client";

import React, { Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ThemedLayoutV2, ThemedSiderV2, ThemedTitleV2 } from "@refinedev/antd";
import { Spin, Layout, Typography, Menu } from "antd";
import { HomeOutlined, BookOutlined, CalendarOutlined, FileTextOutlined } from "@ant-design/icons";
import { useGetIdentity, useGo, Authenticated } from "@refinedev/core";
import { CustomHeader } from "@components/header";
import { CustomFooter } from "@components/footer";
import { StageNoteBrand } from "@components/StageNoteBrand";
import { ModernLayout } from "./modern-layout";
import { TermsGate } from "../terms-gate/TermsGate";
import { StageNoteReleaseNotes } from "../releaseNotes/StageNoteReleaseNotes";
import { DataManagerProvider } from "@contexts/data-manager";
import { User } from "../../types";

const { Header, Sider } = Layout;
const { Title } = Typography;

interface LayoutWrapperProps {
  children: React.ReactNode;
}

const PageLoader = () => (
  <div style={{ 
    display: "flex", 
    justifyContent: "center", 
    alignItems: "center", 
    minHeight: "400px" 
  }}>
    <Spin size="large">
      <div style={{ padding: '50px' }}>Loading...</div>
    </Spin>
  </div>
);

// Full screen loader for authentication checks
const AuthLoader = () => (
  <div style={{
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#fff",
    zIndex: 9999
  }}>
    <Spin size="large">
      <div style={{ padding: '50px' }}>Verifying authentication...</div>
    </Spin>
  </div>
);

// Simple header for authentication pages
const AuthHeader = () => (
  <Header 
    style={{ 
      background: "#fff", 
      padding: "0 24px", 
      boxShadow: "0 2px 8px #f0f1f2",
      display: "flex",
      alignItems: "center",
      height: "64px"
    }}
  >
    <StageNoteBrand size="sm" theme="light" />
  </Header>
);

// Simple footer for authentication pages
const AuthFooter = () => (
  <div style={{ 
    textAlign: "center", 
    padding: "20px", 
    color: "#666",
    fontSize: "14px"
  }}>
    © 2026 StageNote.ai. All rights reserved.
  </div>
);

// Layout for authentication pages (login, register, etc.)
const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Layout style={{ minHeight: "100vh" }}>
      <AuthHeader />
      <Layout.Content style={{ 
        flex: 1,
        display: "flex",
        flexDirection: "column"
      }}>
        {children}
      </Layout.Content>
      <AuthFooter />
    </Layout>
  );
};

const StudentPortalSider: React.FC = () => {
  const go = useGo();
  const location = useLocation();
  const pathname = location.pathname;

  const studentMenuItems = [
    {
      key: '/student-portal',
      icon: <HomeOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/calendar',
      icon: <CalendarOutlined />,
      label: 'Calendar',
    },
    {
      key: '/lessons',
      icon: <BookOutlined />,
      label: 'Lessons',
    },
    {
      key: '/invoices',
      icon: <FileTextOutlined />,
      label: 'Invoices',
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    go({ to: key });
  };

  return (
    <Sider
      theme="light"
      width={200}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 64,
        bottom: 0,
      }}
    >
      <div style={{ padding: "16px", borderBottom: "1px solid #f0f0f0" }}>
        <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
          Student Portal
        </Title>
      </div>
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[pathname]}
        items={studentMenuItems}
        onClick={handleMenuClick}
      />
    </Sider>
  );
};

export const LayoutWrapper: React.FC<LayoutWrapperProps> = ({ children }) => {
  const location = useLocation();
  const pathname = location.pathname;
  
  // Root page (loading page) should have no navigation at all
  if (pathname === '/') {
    return <>{children}</>;
  }
  
  // Check if current route is an authentication page
  const isAuthPage = pathname?.startsWith('/login') || pathname === '/register' || pathname === '/forgot-password' || pathname === '/confirm-signup';
  
  if (isAuthPage) {
    return <AuthLayout>{children}</AuthLayout>;
  }
  
  // Note: Public booking pages (/book/o/:code, /book/f/:code) are now in the separate studentBookingApp
  // They are no longer handled by this layout

  // For all protected routes, use Refine's Authenticated component
  // DataManagerProvider loads shared data (students, locations, series) once for all pages
  return (
    <Authenticated 
      key="authenticated-routes" 
      redirectOnFail="/login"
      loading={<AuthLoader />}
    >
      <TermsGate>
        <StageNoteReleaseNotes>
        <DataManagerProvider>
          <ModernLayout>
            <Suspense fallback={<PageLoader />}>
              {children}
            </Suspense>
          </ModernLayout>
        </DataManagerProvider>
        </StageNoteReleaseNotes>
      </TermsGate>
    </Authenticated>
  );
}; 